import React, { FC, useContext, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Hidden from '@mui/material/Hidden';
import { useTranslation } from 'react-i18next';
import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { CurrencyContext } from 'context/currency';
import { LabOrderContext } from './lab-order-context';
import PatientClinicStep from 'components/order-flow/lab-order/patient-lab-step/patient-lab-step.component';
import ConstructionsStep from 'components/order-flow/lab-order/construction-step/constructions-step.component';
import DeliveryStep from 'components/order-flow/lab-order/delivery-step/delivery-step.component';
import ConfirmStep from 'components/order-flow/lab-order/confirm-step/confirm-step.component';
import ProductionPlanningStep from 'components/order-flow/lab-order/production-planning-step/production-planning-step.component';

const LabOrder: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const [currency, setCurrency] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const { isProductionPlanningEnabled } = useContext(LabOrderContext);

    const getSteps = () => {
        const steps = [
            translate('orders:PATIENT_CLINIC'),
            translate('orders:CONSTRUCTIONS'),
            translate('orders:DELIVERY_FILES'),
        ];
        if (isProductionPlanningEnabled) {
            steps.push(translate('orders:PRODUCTION_PLANNING'));
        }
        steps.push(translate('orders:CONFIRM'));
        return steps;
    };

    useEffect(() => {
        setActiveStep(0);
    }, []);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeStep]);

    const getStepContent = () => {
        if (isProductionPlanningEnabled) {
            switch (activeStep) {
                case 0:
                    return <PatientClinicStep onNextClick={handleNext} />;
                case 1:
                    return <ConstructionsStep onBackClick={handleBack} onNextClick={handleNext} />;
                case 2:
                    return (
                        <DeliveryStep
                            onBackClick={handleBack}
                            onNextClick={handleNext}
                            hideDeliveryDates
                            hideWorkflow
                        />
                    );
                case 3:
                    return <ProductionPlanningStep onBackClick={handleBack} onNextClick={handleNext} />;
                case 4:
                    return <ConfirmStep onBackClick={handleBack} />;
                default:
                    return 'Unknown stepIndex';
            }
        }

        switch (activeStep) {
            case 0:
                return <PatientClinicStep onNextClick={handleNext} />;
            case 1:
                return <ConstructionsStep onBackClick={handleBack} onNextClick={handleNext} />;
            case 2:
                return <DeliveryStep onBackClick={handleBack} onNextClick={handleNext} />;
            case 3:
                return <ConfirmStep onBackClick={handleBack} />;
            default:
                return 'Unknown stepIndex';
        }
    };

    const steps = getSteps();

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStepperClick = (step: number) => {
        if (step < activeStep) {
            setActiveStep(step);
        }
    };

    return (
        <Box sx={{ maxWidth: '1400px' }} display="flex" flexDirection="column" mx="auto">
            <CurrencyContext.Provider
                value={{
                    currency,
                    setCurrency: (currency: string) => {
                        setCurrency(currency);
                    },
                }}
            >
                <Box mb={2}>
                    <Hidden mdDown>
                        <Stepper activeStep={activeStep}>
                            {steps.map((label, index) => (
                                <Step sx={{ cursor: 'pointer' }} key={label} onClick={() => handleStepperClick(index)}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Hidden>
                </Box>
                <Paper elevation={1}>
                    <Box p={3}>{getStepContent()}</Box>
                </Paper>
            </CurrencyContext.Provider>
        </Box>
    );
};

export default LabOrder;
